import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Activity,
  Clock,
  MapPin,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Users,
  Calendar,
  BarChart3,
  PieChart,
  Zap,
  Search,
  Filter,
  Download,
  RefreshCw,
  Map as MapIcon,
  BrainCircuit,
  Loader2
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart as RePieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { cn } from '../ui/utils';
import { db } from '../../utils/firebase/client';
import { collection, query, where, getDocs, orderBy, limit, Timestamp, onSnapshot } from 'firebase/firestore';
import { getCompanyId } from '../../utils/tenantContext';
import { formatDistanceToNow } from 'date-fns';

interface WellCheckDashboardProps {
  wells: any[];
  pumpers: any[];
  isDarkMode?: boolean;
}

const COLORS = ['#3b82f6', '#22c55e', '#eab308', '#ef4444'];

export default function WellCheckDashboard({ wells, pumpers, isDarkMode = false }: WellCheckDashboardProps) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeTab, setActiveTab] = useState<'overview' | 'map' | 'analytics'>('overview');
  const [loading, setLoading] = useState(true);

  // Real data state
  const [todayChecks, setTodayChecks] = useState(0);
  const [hourlyData, setHourlyData] = useState<any[]>([]);
  const [issuesCount, setIssuesCount] = useState(0);
  const [activePumpers, setActivePumpers] = useState(0);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [statusDistribution, setStatusDistribution] = useState<any[]>([]);
  const [pumperPerformance, setPumperPerformance] = useState<any[]>([]);
  const [onTimeRate, setOnTimeRate] = useState(0);

  const companyId = getCompanyId();

  // Fetch real data from Firestore
  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    const loadDashboardData = async () => {
      setLoading(true);
      try {
        // Get today's date range
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayEnd = new Date();
        todayEnd.setHours(23, 59, 59, 999);

        // 1. Count production entries for today across all wells
        let totalChecks = 0;
        let issuesFound = 0;
        const hourlyBuckets: Record<number, { checks: number; issues: number }> = {};
        const activityLog: any[] = [];
        const pumperStats: Record<string, { name: string; completed: number; onTime: number }> = {};
        let normalCount = 0;
        let issueCount = 0;
        let criticalCount = 0;

        // Initialize hourly buckets
        for (let h = 6; h <= 18; h++) {
          hourlyBuckets[h] = { checks: 0, issues: 0 };
        }

        // Query production entries for each well
        for (const well of wells) {
          try {
            const entriesRef = collection(db, `companies/${companyId}/wells/${well.id}/production_entries`);
            const q = query(
              entriesRef,
              where('entryDate', '>=', todayStart.toISOString().split('T')[0]),
              orderBy('entryDate', 'desc'),
              limit(50)
            );

            const snapshot = await getDocs(q);

            snapshot.forEach(doc => {
              const data = doc.data();
              totalChecks++;

              // Get the hour of the entry
              const entryTime = data.timestamp?.toDate?.() || data.createdAt?.toDate?.() || new Date();
              const hour = entryTime.getHours();
              if (hourlyBuckets[hour]) {
                hourlyBuckets[hour].checks++;
              }

              // Check for issues (notes or maintenance flags)
              const hasIssue = data.notes && data.notes.toLowerCase().includes('issue') ||
                data.notes && data.notes.toLowerCase().includes('problem') ||
                data.maintenanceNeeded;

              if (hasIssue) {
                issuesFound++;
                if (hourlyBuckets[hour]) hourlyBuckets[hour].issues++;
                issueCount++;
              } else {
                normalCount++;
              }

              // Track pumper performance
              const pumperId = data.enteredBy || data.userId || 'unknown';
              const pumperName = data.enteredByName || data.userName || 'Unknown User';
              if (!pumperStats[pumperId]) {
                pumperStats[pumperId] = { name: pumperName, completed: 0, onTime: 100 };
              }
              pumperStats[pumperId].completed++;

              // Add to activity log (limit to most recent 10)
              if (activityLog.length < 10) {
                activityLog.push({
                  id: doc.id,
                  user: pumperName,
                  action: `Checked ${well.name || well.id}`,
                  time: formatDistanceToNow(entryTime, { addSuffix: true }),
                  status: hasIssue ? 'critical' : 'normal',
                  timestamp: entryTime
                });
              }
            });
          } catch (err) {
          }
        }

        // Sort activity log by timestamp
        activityLog.sort((a, b) => b.timestamp - a.timestamp);

        // Convert hourly buckets to chart data
        const chartData = Object.entries(hourlyBuckets).map(([hour, data]) => ({
          hour: `${hour}:00`,
          checks: data.checks,
          issues: data.issues
        }));

        // Convert pumper stats to chart data
        const pumperChartData = Object.values(pumperStats)
          .sort((a, b) => b.completed - a.completed)
          .slice(0, 5);

        // Get count of active users (pumpers who have logged entries today)
        const activeUserCount = Object.keys(pumperStats).length;

        // Calculate on-time rate (entries before 6pm)
        const earlyEntries = totalChecks > 0 ?
          Object.entries(hourlyBuckets)
            .filter(([h]) => parseInt(h) < 18)
            .reduce((sum, [, d]) => sum + d.checks, 0) : 0;
        const ontimePercent = totalChecks > 0 ? Math.round((earlyEntries / totalChecks) * 100) : 100;

        // Update state
        setTodayChecks(totalChecks);
        setIssuesCount(issuesFound);
        setHourlyData(chartData);
        setActivePumpers(activeUserCount > 0 ? activeUserCount : pumpers.length);
        setRecentActivity(activityLog);
        setOnTimeRate(ontimePercent);
        setPumperPerformance(pumperChartData);
        setStatusDistribution([
          { name: 'Normal', value: normalCount },
          { name: 'Issues', value: issueCount },
          { name: 'Critical', value: criticalCount }
        ]);

      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();

    // Refresh every 60 seconds
    const interval = setInterval(loadDashboardData, 60000);
    return () => clearInterval(interval);
  }, [companyId, wells]);

  // Clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleRefresh = async () => {
    // Trigger re-fetch by updating a dummy state
    setLoading(true);
    // The useEffect will reload data since we're setting loading
    setTimeout(() => setLoading(false), 500);
  };

  const stats = [
    {
      label: 'Total Checks Today',
      value: loading ? '...' : todayChecks.toString(),
      trend: todayChecks > 0 ? '+' + Math.round(todayChecks * 0.1) + '%' : 'N/A',
      trendUp: true,
      icon: CheckCircle,
      color: 'text-blue-500',
      bg: 'bg-blue-500/10'
    },
    {
      label: 'On-Time Rate',
      value: loading ? '...' : `${onTimeRate}%`,
      trend: onTimeRate >= 90 ? 'Excellent' : 'Needs Attention',
      trendUp: onTimeRate >= 90,
      icon: Clock,
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10'
    },
    {
      label: 'Issues Found',
      value: loading ? '...' : issuesCount.toString(),
      trend: issuesCount === 0 ? 'Clear' : `${issuesCount} today`,
      trendUp: issuesCount === 0,
      icon: AlertTriangle,
      color: 'text-orange-500',
      bg: 'bg-orange-500/10'
    },
    {
      label: 'Active Pumpers',
      value: loading ? '...' : activePumpers.toString(),
      trend: 'Online',
      trendUp: true,
      icon: Users,
      color: 'text-purple-500',
      bg: 'bg-purple-500/10'
    },
  ];

  return (
    <div className={cn("space-y-6 animate-in fade-in duration-500", isDarkMode ? "text-white" : "text-slate-900")}>

      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Activity className="h-8 w-8 text-blue-600" />
            Well Check Command Center
          </h1>
          <p className={cn("text-sm mt-1 font-mono", isDarkMode ? "text-slate-400" : "text-slate-500")}>
            LIVE OPERATIONS • {currentTime.toLocaleDateString()} • {currentTime.toLocaleTimeString()}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveTab('overview')}
            className={activeTab === 'overview' ? 'bg-blue-600' : ''}
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Overview
          </Button>
          <Button
            variant={activeTab === 'map' ? 'default' : 'outline'}
            onClick={() => setActiveTab('map')}
            className={activeTab === 'map' ? 'bg-blue-600' : ''}
          >
            <MapIcon className="h-4 w-4 mr-2" />
            Live Map
          </Button>
          <Button
            variant={activeTab === 'analytics' ? 'default' : 'outline'}
            onClick={() => setActiveTab('analytics')}
            className={activeTab === 'analytics' ? 'bg-blue-600' : ''}
          >
            <PieChart className="h-4 w-4 mr-2" />
            Analytics
          </Button>
          <Button variant="outline" size="icon" onClick={handleRefresh} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <Card key={index} className={cn("border-l-4", isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white")}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className={cn("text-sm font-medium", isDarkMode ? "text-slate-400" : "text-slate-500")}>{stat.label}</p>
                  <h3 className="text-3xl font-bold mt-2">{stat.value}</h3>
                  <div className="flex items-center mt-1 gap-1">
                    <TrendingUp className={cn("h-3 w-3", stat.trendUp ? "text-emerald-500" : "text-red-500")} />
                    <span className={cn("text-xs font-bold", stat.trendUp ? "text-emerald-500" : "text-red-500")}>
                      {stat.trend}
                    </span>
                    <span className={cn("text-xs ml-1", isDarkMode ? "text-slate-500" : "text-slate-400")}>vs yesterday</span>
                  </div>
                </div>
                <div className={cn("p-3 rounded-xl", stat.bg)}>
                  <stat.icon className={cn("h-6 w-6", stat.color)} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Left Column: Charts */}
        <div className="lg:col-span-2 space-y-6">
          {/* Hourly Volume Chart */}
          <Card className={cn(isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white")}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Check Volume (Hourly)</span>
                <Badge variant="outline" className="font-mono">LIVE</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] w-full">
                {loading ? (
                  <div className="h-full flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                  </div>
                ) : hourlyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={hourlyData}>
                      <defs>
                        <linearGradient id="colorChecks" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#334155" : "#e2e8f0"} />
                      <XAxis dataKey="hour" stroke={isDarkMode ? "#94a3b8" : "#64748b"} />
                      <YAxis stroke={isDarkMode ? "#94a3b8" : "#64748b"} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: isDarkMode ? '#1e293b' : '#fff',
                          borderColor: isDarkMode ? '#334155' : '#e2e8f0',
                          color: isDarkMode ? '#fff' : '#000'
                        }}
                      />
                      <Area type="monotone" dataKey="checks" stroke="#3b82f6" fillOpacity={1} fill="url(#colorChecks)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-slate-400">
                    <CheckCircle className="h-12 w-12 mb-2 opacity-50" />
                    <p>No well checks recorded today</p>
                    <p className="text-sm">Data will appear as pumpers submit entries</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Pumper Performance */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className={cn(isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white")}>
              <CardHeader>
                <CardTitle>Pumper Efficiency</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px] w-full">
                  {loading ? (
                    <div className="h-full flex items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                    </div>
                  ) : pumperPerformance.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={pumperPerformance} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#334155" : "#e2e8f0"} />
                        <XAxis type="number" stroke={isDarkMode ? "#94a3b8" : "#64748b"} />
                        <YAxis dataKey="name" type="category" stroke={isDarkMode ? "#94a3b8" : "#64748b"} width={80} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDarkMode ? '#1e293b' : '#fff',
                            borderColor: isDarkMode ? '#334155' : '#e2e8f0'
                          }}
                        />
                        <Bar dataKey="completed" fill="#22c55e" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-400">
                      <Users className="h-12 w-12 mb-2 opacity-50" />
                      <p className="text-center">No pumper activity yet today</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className={cn(isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white")}>
              <CardHeader>
                <CardTitle>Check Status Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px] w-full">
                  {loading ? (
                    <div className="h-full flex items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                    </div>
                  ) : statusDistribution.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <RePieChart>
                        <Pie
                          data={statusDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {COLORS.map((color, index) => (
                            <Cell key={`cell-${index}`} fill={color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </RePieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-400">
                      <PieChart className="h-12 w-12 mb-2 opacity-50" />
                      <p className="text-center">No data to display</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Right Column: Feed & AI */}
        <div className="space-y-6">

          {/* AI Insights Card */}
          <Card className={cn("border-l-4 border-l-purple-500", isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white")}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-purple-500">
                <BrainCircuit className="h-5 w-5" />
                Smart Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {issuesCount > 0 ? (
                  <div className={cn("p-3 rounded-lg text-sm", isDarkMode ? "bg-slate-800" : "bg-red-50")}>
                    <p className="font-bold mb-1">⚠️ Issues Detected</p>
                    <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-600")}>
                      {issuesCount} well check{issuesCount > 1 ? 's' : ''} reported issues today. Review the activity feed for details.
                    </p>
                  </div>
                ) : todayChecks > 0 ? (
                  <div className={cn("p-3 rounded-lg text-sm", isDarkMode ? "bg-slate-800" : "bg-green-50")}>
                    <p className="font-bold mb-1">✅ Operations Normal</p>
                    <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-600")}>
                      All {todayChecks} well checks today reported no issues. Great work!
                    </p>
                  </div>
                ) : (
                  <div className={cn("p-3 rounded-lg text-sm", isDarkMode ? "bg-slate-800" : "bg-amber-50")}>
                    <p className="font-bold mb-1">📝 Waiting for Data</p>
                    <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-600")}>
                      No well checks recorded yet today. Insights will appear as data comes in.
                    </p>
                  </div>
                )}
                {activePumpers > 0 && (
                  <div className={cn("p-3 rounded-lg text-sm", isDarkMode ? "bg-slate-800" : "bg-purple-50")}>
                    <p className="font-bold mb-1">👥 Team Status</p>
                    <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-600")}>
                      {activePumpers} pumper{activePumpers > 1 ? 's have' : ' has'} submitted entries today.
                      {wells.length > 0 && ` ${wells.length} wells being monitored.`}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Live Feed */}
          <Card className={cn("h-[500px] flex flex-col", isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white")}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Live Feed</span>
                <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
              {loading ? (
                <div className="h-full flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                </div>
              ) : recentActivity.length > 0 ? (
                <div className="space-y-4">
                  {recentActivity.map((item) => (
                    <div key={item.id} className="flex gap-3 items-start">
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0",
                        item.status === 'critical' ? "bg-red-100 text-red-600" :
                          item.status === 'success' ? "bg-green-100 text-green-600" :
                            item.status === 'info' ? "bg-blue-100 text-blue-600" :
                              "bg-slate-100 text-slate-600"
                      )}>
                        {item.user.charAt(0)}
                      </div>
                      <div>
                        <p className={cn("text-sm font-medium", isDarkMode ? "text-white" : "text-slate-900")}>
                          {item.user}
                        </p>
                        <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                          {item.action}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-1">{item.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-slate-400">
                  <Activity className="h-12 w-12 mb-2 opacity-50" />
                  <p className="text-center">No activity yet today</p>
                  <p className="text-sm text-center">Activity will appear as pumpers check wells</p>
                </div>
              )}
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
}